/**
 * /gallery dev route (U5): every design-system component, in both themes,
 * with realistic estimate data. U6 mounts this at `/gallery`
 * (lazy `import('../ui/gallery/GalleryPage')` — default export provided).
 *
 * All figures on this page are SAMPLE data standing in for a contractor's own
 * library costs. Job Site AI never suggests market pricing, and preview math
 * here is display-only — production totals derive from the shared engine (U3).
 */
import { useMemo, useRef, useState, type JSX, type ReactNode } from 'react';
import { BRAND } from '@buildledger/shared';
import '../../styles/index.css';
import { Button } from '../Button';
import { ConfirmDialog } from '../ConfirmDialog';
import { CurrencyInput } from '../CurrencyInput';
import { DataTable, type DataTableColumn } from '../DataTable';
import { DatePicker } from '../DatePicker';
import { FileDrop } from '../FileDrop';
import { Input } from '../Input';
import {
  LineItemGrid,
  type LineGridColumn,
  type LineGridSuggestion,
  type LineGridValue,
} from '../LineItemGrid';
import { MarkupMarginToggle, type PricingMode } from '../MarkupMarginToggle';
import { Modal } from '../Modal';
import { NumericInput } from '../NumericInput';
import { Select } from '../Select';
import { Spinner } from '../Spinner';
import { StatusBadge } from '../StatusBadge';
import { Tabs } from '../Tabs';
import { ToastProvider, useToast } from '../Toast';
import { cx } from '../cx';
import { formatCurrency, roundTo } from '../format';
import { setDocumentTheme, type ThemeName } from '../theme';
import { IconPlus } from '../icons';
import {
  ALL_STATUSES,
  CATALOG_DEFAULTS,
  SAMPLE_ESTIMATES,
  SAMPLE_LINES,
  searchCatalog,
  type SampleEstimate,
  type SampleLine,
} from './sample-data';

type GalleryMode = 'split' | 'dark' | 'light';

/* ------------------------------------------------------------------------- */

function Section({
  index,
  title,
  desc,
  children,
}: {
  index: number;
  title: string;
  desc?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="bl-g-section">
      <div className="bl-g-section__eyebrow">{String(index).padStart(2, '0')} · Component</div>
      <h2 className="bl-g-section__title">{title}</h2>
      {desc && <p className="bl-g-section__desc">{desc}</p>}
      {children}
    </section>
  );
}

function Card({ children, className }: { children: ReactNode; className?: string }): JSX.Element {
  return <div className={cx('bl-glass bl-g-card', className)}>{children}</div>;
}

/* ---- sections ----------------------------------------------------------- */

function FoundationsSection(): JSX.Element {
  const swatches = [
    { name: 'Amber', token: '--bl-amber' },
    { name: 'Orange', token: '--bl-orange' },
    { name: 'Canvas', token: '--bl-bg' },
    { name: 'Surface', token: '--bl-surface' },
    { name: 'Ink', token: '--bl-ink' },
    { name: 'OK', token: '--bl-ok' },
    { name: 'Warn', token: '--bl-warn' },
    { name: 'Danger', token: '--bl-danger' },
  ];
  return (
    <Section
      index={1}
      title="Foundations"
      desc="Yellow/orange/black liquid glass: warm blacks, translucent blurred surfaces, amber light, and construction-grade tabular numerals for money."
    >
      <Card>
        <div className="bl-g-swatches">
          {swatches.map((s) => (
            <div key={s.token}>
              <div className="bl-g-swatch__chip" style={{ background: `var(${s.token})` }} />
              <div className="bl-g-swatch__name">{s.name}</div>
              <div className="bl-g-swatch__token">{s.token}</div>
            </div>
          ))}
        </div>
      </Card>
      <Card>
        <div className="bl-g-row bl-g-row--top" style={{ gap: '2.5rem' }}>
          <div>
            <div className="bl-g-type-display">
              Build the <em>number</em> they sign.
            </div>
            <p className="bl-g-caption">Display type — tight tracking, weight 780.</p>
          </div>
          <div className="bl-num bl-g-money">
            <small>Estimate total</small>
            $18,420.00
            <p className="bl-g-caption">Money is always monospace + tabular.</p>
          </div>
        </div>
      </Card>
    </Section>
  );
}

function ButtonsSection(): JSX.Element {
  const [busy, setBusy] = useState(false);
  return (
    <Section index={2} title="Button" desc="Four variants, three sizes, loading state.">
      <Card>
        <div className="bl-g-stack">
          <div className="bl-g-row">
            <Button>Send proposal</Button>
            <Button variant="secondary">Save draft</Button>
            <Button variant="ghost">Cancel</Button>
            <Button variant="danger">Delete estimate</Button>
          </div>
          <div className="bl-g-row">
            <Button size="sm" variant="secondary">
              Small
            </Button>
            <Button size="md" variant="secondary">
              Medium
            </Button>
            <Button size="lg" variant="secondary">
              Large
            </Button>
            <Button iconStart={<IconPlus size={14} />} variant="secondary">
              New estimate
            </Button>
          </div>
          <div className="bl-g-row">
            <Button
              loading={busy}
              onClick={() => {
                setBusy(true);
                setTimeout(() => setBusy(false), 1600);
              }}
            >
              {busy ? 'Sending…' : 'Click to load'}
            </Button>
            <Button disabled>Disabled</Button>
            <Button variant="secondary" disabled>
              Disabled
            </Button>
            <Spinner size={18} />
          </div>
        </div>
      </Card>
    </Section>
  );
}

function InputsSection(): JSX.Element {
  const [qty, setQty] = useState<number | null>(32);
  const [rate, setRate] = useState<number | null>(78);
  const [unit, setUnit] = useState<string | null>('hr');
  const [date, setDate] = useState<string | null>('2026-08-03');
  return (
    <Section
      index={3}
      title="Inputs"
      desc="Input, NumericInput, CurrencyInput, Select, DatePicker. Numeric entry commits on blur/Enter, steps with arrow keys, and always renders tabular numerals."
    >
      <Card>
        <div className="bl-g-grid">
          <Input label="Client name" placeholder="Hendricks Residence" hint="As it appears on the proposal." full />
          <Input
            label="Email"
            type="email"
            defaultValue="not-an-email"
            error="Enter a valid email address."
            full
          />
          <Input label="License #" prefix="MN" placeholder="EA00xxxx" full />
          <NumericInput
            label="Quantity"
            value={qty}
            onValueChange={setQty}
            decimals={4}
            trim
            suffix="hr"
            hint="Arrow keys step; ⇧ text parses $ and commas."
            full
          />
          <CurrencyInput label="Unit cost" value={rate} onValueChange={setRate} full />
          <Select
            label="Unit"
            value={unit}
            onChange={setUnit}
            full
            options={[
              { value: 'hr', label: 'Hour', hint: 'hr' },
              { value: 'ea', label: 'Each', hint: 'ea' },
              { value: 'ft', label: 'Linear foot', hint: 'ft' },
              { value: 'sf', label: 'Square foot', hint: 'sf' },
              { value: 'ls', label: 'Lump sum', hint: 'ls' },
            ]}
          />
          <DatePicker
            label="Proposal expires"
            value={date}
            onChange={setDate}
            hint="Type M/D/YYYY or pick — arrows navigate the calendar."
            full
          />
        </div>
      </Card>
    </Section>
  );
}

function PricingSection(): JSX.Element {
  const [mode, setMode] = useState<PricingMode>('markup');
  const [pct, setPct] = useState<number | null>(25);
  return (
    <Section
      index={4}
      title="MarkupMarginToggle"
      desc="Markup vs margin must never be ambiguous: the control names the active mode, spells out the formula, and shows the equivalent in the other mode."
    >
      <Card>
        <MarkupMarginToggle
          mode={mode}
          pct={pct}
          onModeChange={setMode}
          onPctChange={setPct}
          label="Estimate pricing"
        />
      </Card>
    </Section>
  );
}

function StructureSection(): JSX.Element {
  const [tab, setTab] = useState('lines');
  return (
    <Section index={5} title="Tabs + StatusBadge" desc="Sliding glass tab indicator; status chips for every product status, including compliance verification labels.">
      <Card>
        <div className="bl-g-stack">
          <Tabs
            aria-label="Estimate editor sections"
            value={tab}
            onChange={setTab}
            items={[
              { id: 'lines', label: 'Lines', badge: 7 },
              { id: 'notes', label: 'Notes', badge: 3 },
              { id: 'versions', label: 'Versions' },
              { id: 'proposal', label: 'Proposal' },
              { id: 'locked', label: 'Locked', disabled: true },
            ]}
          />
          <div className="bl-g-row">
            {ALL_STATUSES.map((status) => (
              <StatusBadge key={status} status={status} />
            ))}
          </div>
          <p className="bl-g-caption">
            Verification labels (verified / unverified draft / federal anchor) mark compliance
            content status — informational tooling, never a compliance assertion.
          </p>
        </div>
      </Card>
    </Section>
  );
}

function FeedbackSection(): JSX.Element {
  const [open, setOpen] = useState(false);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const { toast } = useToast();
  return (
    <Section index={6} title="Modal + Toast" desc="Portal overlays follow the document theme. Modal traps focus and restores it on close.">
      <Card>
        <div className="bl-g-row">
          <Button variant="secondary" onClick={() => setOpen(true)}>
            Open modal
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              toast({
                title: 'Proposal sent',
                description: 'E-20260718-004 emailed to the client (captured in dev outbox).',
                tone: 'success',
              })
            }
          >
            Success toast
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              toast({
                title: 'Autosave failed',
                description: 'Your edits are kept locally — retrying.',
                tone: 'danger',
              })
            }
          >
            Error toast
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              toast({ title: 'Sub quote received', description: 'Duluth Concrete submitted a quote.', tone: 'info' })
            }
          >
            Info toast
          </Button>
        </div>
      </Card>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Send estimate E-20260718-004?"
        initialFocusRef={confirmRef}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Keep editing
            </Button>
            <Button ref={confirmRef} onClick={() => setOpen(false)}>
              Freeze &amp; send
            </Button>
          </>
        }
      >
        <p>
          Sending freezes version 3 as an immutable, hash-stamped document and emails the client a
          tokenized proposal link. Further edits will require a revision.
        </p>
        <p className="bl-g-caption">Escape closes · Tab cycles inside · focus returns on close.</p>
      </Modal>
    </Section>
  );
}

function ConfirmSection(): JSX.Element {
  const [open, setOpen] = useState<'default' | 'danger' | null>(null);
  const [busy, setBusy] = useState(false);
  const confirm = (): void => {
    setBusy(true);
    window.setTimeout(() => {
      setBusy(false);
      setOpen(null);
    }, 900);
  };
  return (
    <Section index={10} title="ConfirmDialog" desc="Yes/no confirmation over Modal. Danger tone uses the destructive button; busy disables both buttons.">
      <Card>
        <div className="bl-g-row">
          <Button variant="secondary" onClick={() => setOpen('default')}>
            Promote user
          </Button>
          <Button variant="secondary" onClick={() => setOpen('danger')}>
            Demote user
          </Button>
        </div>
      </Card>
      <ConfirmDialog
        open={open !== null}
        title={open === 'danger' ? 'Are you sure you want to demote this user?' : 'Are you sure you want to promote this user?'}
        body={open === 'danger' ? 'Jordan Reyes moves from Foreman to Worker.' : 'Jordan Reyes moves from Worker to Foreman.'}
        confirmLabel={open === 'danger' ? 'Demote' : 'Promote'}
        tone={open === 'danger' ? 'danger' : 'default'}
        busy={busy}
        onConfirm={confirm}
        onCancel={() => setOpen(null)}
      />
    </Section>
  );
}

function TableSection(): JSX.Element {
  const columns: DataTableColumn<SampleEstimate>[] = [
    {
      key: 'number',
      header: 'Estimate',
      mono: true,
      width: '11rem',
      render: (r) => r.number,
      sort: (a, b) => a.number.localeCompare(b.number),
    },
    { key: 'title', header: 'Title', render: (r) => r.title },
    { key: 'client', header: 'Client', render: (r) => r.client },
    {
      key: 'status',
      header: 'Status',
      width: '8.5rem',
      render: (r) => <StatusBadge status={r.status} size="sm" />,
      sort: (a, b) => a.status.localeCompare(b.status),
    },
    {
      key: 'total',
      header: 'Total',
      align: 'end',
      mono: true,
      width: '9rem',
      render: (r) => formatCurrency(r.total),
      sort: (a, b) => a.total - b.total,
    },
  ];
  return (
    <Section index={7} title="DataTable" desc="Sticky glass header, sortable columns, mono numeric columns, clickable rows.">
      <DataTable
        aria-label="Recent estimates"
        columns={columns}
        rows={SAMPLE_ESTIMATES}
        rowKey={(r) => r.id}
        initialSort={{ key: 'number', dir: 'desc' }}
        onRowClick={() => undefined}
      />
      <p className="bl-g-caption">Sample data for component preview — not market pricing.</p>
    </Section>
  );
}

function FileDropSection(): JSX.Element {
  const { toast } = useToast();
  return (
    <Section index={8} title="FileDrop" desc="Drag-and-drop with keyboard access; storage and seam-aware checks live in FilesModule (U11).">
      <Card>
        <FileDrop
          accept=".csv,text/csv"
          multiple
          hint="CSV up to 10 MB — column mapping comes next"
          onFiles={(files) =>
            toast({
              title: `${files.length} file${files.length === 1 ? '' : 's'} selected`,
              description: files.map((f) => f.name).join(', '),
              tone: 'info',
            })
          }
        />
      </Card>
    </Section>
  );
}

let nextLineId = 100;

function LineGridSection(): JSX.Element {
  const [lines, setLines] = useState<SampleLine[]>(SAMPLE_LINES);

  function patchLine(id: string, patch: Partial<SampleLine>): void {
    setLines((prev) =>
      prev.map((line) => {
        if (line.id !== id) return line;
        const next = { ...line, ...patch };
        // PREVIEW math only — production extended prices come from the shared
        // engine (U3); this keeps the demo grid coherent while you type.
        next.extended =
          next.qty !== null && next.unitCost !== null
            ? roundTo(next.qty * next.unitCost, 2)
            : null;
        return next;
      }),
    );
  }

  const columns: LineGridColumn<SampleLine>[] = useMemo(
    () => [
      {
        key: 'description',
        header: 'Description',
        kind: 'text',
        width: 'minmax(17rem, 3fr)',
        placeholder: 'Type to search labor, materials, assemblies…',
        getValue: (r) => r.description,
        getSuggestions: (q) => searchCatalog(q),
      },
      {
        key: 'qty',
        header: 'Qty',
        kind: 'number',
        decimals: 4,
        width: '6.5rem',
        getValue: (r) => r.qty,
      },
      {
        key: 'unit',
        header: 'Unit',
        kind: 'text',
        width: '5rem',
        getValue: (r) => r.unit,
      },
      {
        key: 'unitCost',
        header: 'Unit cost',
        kind: 'currency',
        width: '8.5rem',
        getValue: (r) => r.unitCost,
        placeholder: 'from library',
      },
      {
        key: 'extended',
        header: 'Ext. cost',
        kind: 'currency',
        width: '9rem',
        editable: false,
        getValue: (r) => r.extended,
      },
    ],
    [],
  );

  function handleCommit(rowId: string, columnKey: string, value: LineGridValue): void {
    if (columnKey === 'qty' || columnKey === 'unitCost') {
      patchLine(rowId, { [columnKey]: typeof value === 'number' ? value : null });
    } else if (columnKey === 'description' || columnKey === 'unit') {
      patchLine(rowId, { [columnKey]: value === null ? '' : String(value) });
    }
  }

  function handlePick(rowId: string, _columnKey: string, suggestion: LineGridSuggestion): void {
    const defaults = CATALOG_DEFAULTS[suggestion.id];
    patchLine(rowId, {
      description: suggestion.label,
      unit: defaults?.unit ?? 'ea',
      unitCost: defaults?.unitCost ?? null,
    });
  }

  const subtotal = lines
    .filter((l) => l.tone !== 'alternate')
    .reduce((sum, l) => sum + (l.extended ?? 0), 0);
  const alternates = lines
    .filter((l) => l.tone === 'alternate')
    .reduce((sum, l) => sum + (l.extended ?? 0), 0);

  return (
    <Section
      index={9}
      title="LineItemGrid — flagship"
      desc="Keyboard-first estimate entry: roving tabindex, Enter-to-next-row, arrow navigation, quick-type editing, and a type-ahead library picker in the description cell. Faster than a spreadsheet is the bar."
    >
      <LineItemGrid
        aria-label="Estimate lines"
        columns={columns}
        rows={lines}
        onCellCommit={handleCommit}
        onSuggestionPick={handlePick}
        onAddRow={() => {
          nextLineId += 1;
          setLines((prev) => [
            ...prev,
            { id: `l${nextLineId}`, description: '', qty: null, unit: 'ea', unitCost: null, extended: null },
          ]);
        }}
        onDeleteRow={(id) => setLines((prev) => prev.filter((l) => l.id !== id))}
        rowTone={(r) => r.tone ?? 'default'}
        footer={
          <div className="bl-g-totals bl-num">
            <div className="bl-g-totals__item">
              <div className="bl-g-totals__label">Alternates (excluded)</div>
              <div className="bl-g-totals__value">{formatCurrency(alternates)}</div>
            </div>
            <div className="bl-g-totals__item">
              <div className="bl-g-totals__label">Base cost subtotal</div>
              <div className="bl-g-totals__value bl-g-totals__value--grand">
                {formatCurrency(subtotal)}
              </div>
            </div>
          </div>
        }
      />
      <div className="bl-g-kbd-hints">
        <span>
          <span className="bl-kbd">↑↓←→</span> move
        </span>
        <span>
          <span className="bl-kbd">Enter</span> edit / commit ↓ (adds a row at the end)
        </span>
        <span>
          <span className="bl-kbd">a–z 0–9</span> quick-type
        </span>
        <span>
          <span className="bl-kbd">Tab</span> commit →
        </span>
        <span>
          <span className="bl-kbd">Esc</span> cancel
        </span>
        <span>
          <span className="bl-kbd">⌘⌫</span> delete row
        </span>
      </div>
      <p className="bl-g-caption">
        Sample figures stand in for your own library costs — {BRAND.name} never suggests market
        pricing. Preview math only; production totals derive from the shared engine.
      </p>
    </Section>
  );
}

function GalleryContent(): JSX.Element {
  return (
    <>
      <FoundationsSection />
      <ButtonsSection />
      <InputsSection />
      <PricingSection />
      <StructureSection />
      <FeedbackSection />
      <TableSection />
      <FileDropSection />
      <LineGridSection />
      <ConfirmSection />
    </>
  );
}

function GalleryPane({ theme }: { theme: ThemeName }): JSX.Element {
  return (
    <div className="bl-canvas bl-gallery__pane" data-theme={theme}>
      <div className="bl-gallery__pane-label">{theme} theme</div>
      <GalleryContent />
    </div>
  );
}

export function GalleryPage(): JSX.Element {
  const [mode, setMode] = useState<GalleryMode>('split');

  function changeMode(next: string): void {
    const m = next as GalleryMode;
    setMode(m);
    if (m === 'dark' || m === 'light') setDocumentTheme(m);
  }

  return (
    <ToastProvider>
      <div className={cx('bl-gallery', mode === 'split' && 'bl-gallery--split')}>
        <header className="bl-gallery__topbar">
          <div className="bl-gallery__mark">B</div>
          <div className="bl-gallery__titles">
            <div className="bl-gallery__title">{BRAND.name} design system</div>
            <div className="bl-gallery__subtitle">
              U5 gallery — every component, both themes, realistic estimate data
            </div>
          </div>
          <div className="bl-gallery__spacer" />
          <Tabs
            aria-label="Gallery theme mode"
            size="sm"
            value={mode}
            onChange={changeMode}
            items={[
              { id: 'split', label: 'Split' },
              { id: 'dark', label: 'Dark' },
              { id: 'light', label: 'Light' },
            ]}
          />
        </header>
        <div className="bl-gallery__panes">
          {mode === 'split' ? (
            <>
              <GalleryPane theme="dark" />
              <GalleryPane theme="light" />
            </>
          ) : (
            <GalleryPane theme={mode} />
          )}
        </div>
      </div>
    </ToastProvider>
  );
}

export default GalleryPage;
